<template>
  <canvas ref="canvas" class="game-canvas" />

  <Transition name="fade">
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-content">
        <div class="loading-logo">Lasantha<span class="accent">.dev</span></div>
        <Transition name="msg" mode="out-in">
          <p class="loading-sub" :key="loadingMessage">{{ loadingMessage }}</p>
        </Transition>
        <div class="loading-bar-track">
          <div class="loading-bar-fill"></div>
        </div>
      </div>
    </div>
  </Transition>

  <div v-if="!isLoading && showControls" class="controls-overlay">
    <div class="welcome-text">
      <h1>Welcome to <span class="accent">Lasantha.dev</span></h1>
      <p class="tagline">Explore the island to discover my skills, projects & story.</p>
    </div>
    <div class="controls-panel">
      <h2>Controls</h2>
      <ul>
        <li><span class="key">W A S D</span> Move</li>
        <li><span class="key">Shift</span> + <span class="key">W/A/D</span> Run</li>
        <li><span class="key">Shift</span> + <span class="key">Space</span> Running Jump</li>
        <li><span class="key">B</span> Bonus Key</li>
        <li><span class="key">Scroll</span> Zoom in / out</li>
        <li><span class="key">Click</span> Lock mouse / look around</li>
        <li><span class="key">ESC</span> Release mouse</li>
      </ul>
      <p class="desktop-note">🖥️ Best experienced on desktop with a keyboard & mouse.</p>
      <button class="start-btn" @click="startExploring">Explore Lasantha's Skill Island</button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, onUnmounted } from 'vue';
import { BasicScene } from '@/scripts/scene/BasicScene';

const LOADING_MESSAGES = [
  'Sailing to the island…',
  'Painting the sky…',
  'Convincing seagulls to relocate…',
  'Polishing the contact stones…',
  'Lighting the lighthouse…',
  'Untangling the rigging…',
  'Charting the coastline…',
  'Briefing the crew…',
  'Negotiating with the kraken…',
  'Compiling adventures…',
  'Brewing the coffee…',
  'Greasing the cannons…',
  'Tuning the wind…',
  'Waking the parrots…',
  'Loading rum and pixels…',
];

export default defineComponent({
  name: 'HomeView',
  setup() {
    const canvas = ref<HTMLCanvasElement | null>(null);
    const showControls = ref(true);
    const isLoading = ref(true);
    const loadingMessage = ref(LOADING_MESSAGES[0]);
    const bgMusic = new Audio('/music/bg_music.mp3');
    bgMusic.loop = true;
    bgMusic.volume = 0.20;

    let messageTimer: number | undefined;

    function stopMessageRotation() {
      if (messageTimer !== undefined) {
        window.clearInterval(messageTimer);
        messageTimer = undefined;
      }
    }

    onMounted(() => {
      // Start with a random message so refreshes don't always show the same first line.
      let idx = Math.floor(Math.random() * LOADING_MESSAGES.length);
      loadingMessage.value = LOADING_MESSAGES[idx];

      messageTimer = window.setInterval(() => {
        idx = (idx + 1) % LOADING_MESSAGES.length;
        loadingMessage.value = LOADING_MESSAGES[idx];
      }, 2200);

      if (canvas.value) {
        new BasicScene(canvas.value, () => {
          isLoading.value = false;
          stopMessageRotation();
        });
      }
    });

    onUnmounted(() => {
      bgMusic.pause();
      stopMessageRotation();
    });

    function startExploring() {
      showControls.value = false;
      bgMusic.play();
    }

    return { canvas, showControls, isLoading, loadingMessage, startExploring };
  },
});
</script>

<style scoped>
.game-canvas {
  display: block;
  width: 100vw;
  height: 100vh;
  outline: none;
}

.controls-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3rem;
  background: rgba(0, 0, 0, 0.65);
  backdrop-filter: blur(6px);
  z-index: 100;
}

.controls-panel {
  background: rgba(15, 15, 25, 0.92);
  border: 1px solid rgba(255, 160, 60, 0.4);
  border-radius: 12px;
  padding: 2rem 2.5rem;
  color: #f0e0c0;
  min-width: 320px;
  box-shadow: 0 0 40px rgba(255, 120, 20, 0.2);
}

.controls-panel h2 {
  font-size: 1rem;
  margin-bottom: 1.2rem;
  color: rgba(255, 190, 100, 0.6);
  letter-spacing: 0.2em;
  text-transform: uppercase;
  text-align: center;
}

.welcome-text {
  text-align: center;
  margin-bottom: 2.5rem;
}

.welcome-text h1 {
  font-size: 1.8rem;
  font-weight: 800;
  color: #fff5e0;
  letter-spacing: 0.02em;
  margin-bottom: 0.4rem;
}

.accent {
  color: #ffaa40;
}

.tagline {
  font-size: 0.95rem;
  color: rgba(240, 210, 160, 0.8);
  font-style: italic;
}

.controls-panel ul {
  list-style: none;
  padding: 0;
  margin: 0 0 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.controls-panel li {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.95rem;
}

.key {
  display: inline-block;
  background: rgba(255, 160, 60, 0.15);
  border: 1px solid rgba(255, 160, 60, 0.5);
  border-radius: 5px;
  padding: 2px 8px;
  font-family: monospace;
  font-size: 0.85rem;
  color: #ffcc88;
}

.desktop-note {
  text-align: center;
  font-size: 0.82rem;
  color: rgba(240, 200, 140, 0.7);
  margin-bottom: 1rem;
}

.start-btn {
  display: block;
  width: 100%;
  padding: 0.7rem;
  background: rgba(255, 140, 30, 0.85);
  border: none;
  border-radius: 8px;
  color: #fff;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  letter-spacing: 0.05em;
  transition: background 0.2s;
}

.start-btn:hover {
  background: rgba(255, 160, 60, 1);
}

/* ── Loading screen ── */
.loading-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #06080f;
  z-index: 200;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.loading-logo {
  font-size: 2.4rem;
  font-weight: 800;
  color: #fff5e0;
  letter-spacing: 0.04em;
}

.loading-sub {
  font-size: 0.9rem;
  color: rgba(240, 200, 140, 0.6);
  letter-spacing: 0.12em;
  text-transform: uppercase;
  min-height: 1.2em;
  text-align: center;
}

.msg-enter-active,
.msg-leave-active {
  transition: opacity 0.35s ease, transform 0.35s ease;
}
.msg-enter-from {
  opacity: 0;
  transform: translateY(4px);
}
.msg-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.loading-bar-track {
  width: 220px;
  height: 3px;
  background: rgba(255, 160, 60, 0.15);
  border-radius: 2px;
  overflow: hidden;
  margin-top: 0.5rem;
}

.loading-bar-fill {
  height: 100%;
  width: 40%;
  background: #ffaa40;
  border-radius: 2px;
  animation: loading-slide 1.4s ease-in-out infinite;
}

@keyframes loading-slide {
  0%   { transform: translateX(-100%); width: 40%; }
  50%  { width: 70%; }
  100% { transform: translateX(320%); width: 40%; }
}

/* ── Fade transition ── */
.fade-leave-active {
  transition: opacity 0.6s ease;
}
.fade-leave-to {
  opacity: 0;
}
</style>
